package com.nonopn.skydome.common.app.navigation

import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.pages.promocodes.PromoCodeEditScreen
import com.nonopn.skydome.common.app.pages.promocodes.PromoCodesAdminStore
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.promo_admin_edit_title
import com.nonopn.skywatcher.common.app.res.promo_admin_new_title
import com.nonopn.skywatcher.design.appbar.AppBarState
import eu.codlab.navigation.NavTypeWrapper
import eu.codlab.navigation.NavigateTo
import eu.codlab.navigation.NavigateToStack
import eu.codlab.navigation.NavigateWithNavOptions
import eu.codlab.navigation.Route
import eu.codlab.navigation.RouteParameterTo
import eu.codlab.navigation.RouterParameters
import kotlinx.serialization.Serializable
import kotlin.reflect.KType

private const val PromoCodeEditAdminPrefix = "/admin/promotion-code/"

/** Form of a promotion code: [uuid] of an existing code, or [NEW] for a creation. */
@Serializable
data class RoutePromotionCodeEditAdmin(
    val uuid: String,
) : RouteParameterTo {
    companion object {
        const val NEW = "new"
    }
}

object RouterPromotionCodeEditAdmin :
    RouterParameters<RoutePromotionCodeEditAdmin>(RoutePromotionCodeEditAdmin::class) {
    override val typeMap: Map<KType, NavTypeWrapper<Any>> get() = emptyMap()

    override fun routeFrom(parameters: RoutePromotionCodeEditAdmin) = PromoCodeEditAdminPrefix + parameters.uuid

    @Composable
    override fun Show(parameters: RoutePromotionCodeEditAdmin) {
        RoutePromotionCodeEditAdminImpl(parameters).scene()
    }

    override fun navigateTo(instance: RoutePromotionCodeEditAdmin) = NavigateTo(
        route = instance,
        stack = NavigateToStack(
            // Keep the list in the stack so that "back" returns to it
            popBackStack = false,
            options = NavigateWithNavOptions(
                launchSingleTop = true
            )
        )
    )

    override fun isCurrentRoute(routeParameterTo: RouteParameterTo?) = routeParameterTo is RoutePromotionCodeEditAdmin

    override fun isMatching(route: String) = route.startsWith(PromoCodeEditAdminPrefix)

    // Direct entry (link, reload): the uuid is the segment after "/admin/promotion-code/"
    override fun navigateFrom(path: String) = RoutePromotionCodeEditAdmin(
        uuid = path.substringBefore('?').substringBefore('#').removePrefix(PromoCodeEditAdminPrefix)
            .trim('/').substringBefore('/')
    )
}

class RoutePromotionCodeEditAdminImpl(params: RoutePromotionCodeEditAdmin) :
    Route<RoutePromotionCodeEditAdmin>(
        route = PromoCodeEditAdminPrefix + params.uuid,
        params = params,
    ) {
    @Composable
    override fun scene() {
        val appModel: AppModel = LocalApp.current
        val state by appModel.states.collectAsState()
        val isNew = params.uuid == RoutePromotionCodeEditAdmin.NEW
        val existing = if (isNew) null else PromoCodesAdminStore.find(params.uuid)

        if (state.apiProvider?.apiAdmin == null) {
            LaunchedEffect(Unit) { appModel.leaveAdminRoute() }
            return
        }
        // Direct entry on an existing code (the list was never loaded): back to the list
        if (!isNew && existing == null) {
            LaunchedEffect(Unit) { appModel.show(RouterPromotionCodesAdmin.navigateTo()) }
            return
        }

        LaunchedEffect(Unit) {
            appModel.ensureAdminMode()
            appModel.markCurrentRoute(params)
            appModel.setAppBarState(
                AppBarState.Localized(
                    title = if (isNew) Res.string.promo_admin_new_title else Res.string.promo_admin_edit_title,
                    emptyList()
                )
            )
        }

        PromoCodeEditScreen(
            modifier = Modifier.fillMaxSize(),
            existing = existing,
            onDone = { appModel.popBackStack() },
        )
    }
}
